import { useState, useEffect, useRef, useCallback } from "react";
import { PrinterIcon } from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import toast from "react-hot-toast";
import { reportsApi } from "../../api/reports";
import { useSeason } from "../../context/SeasonContext";
import { formatCabinNumber, normalizeDivisionName, parseCabinNumber } from "../../utils/terminology";
import type { CeremonyReportDivision, CeremonyReportCabin, CeremonyReportCamper } from "../../types";

// Print page content dimensions in px (US Letter, 0.5in margins each side)
// Width:  8.5in − 2×0.5in = 7.5in × 96px/in = 720px
// Height: 11in  − 2×0.5in = 10in  × 96px/in = 960px
const PRINT_PAGE_HEIGHT_PX = 960;
const PRINT_PAGE_WIDTH_PX = 720;
const FONT_SIZE_MAX_PT = 18;
const FONT_SIZE_MIN_PT = 12;

// ─── Print styles injected into <head> ───────────────────────────────────────

const PRINT_STYLE = `
@media print {
  @page { size: letter portrait; margin: 0.5in; }
  .no-print { display: none !important; }
  .division-section { break-after: page; page-break-after: always; }
  .division-section:last-child { break-after: auto; page-break-after: auto; }
  .cabin-section { break-inside: avoid; page-break-inside: avoid; }
  body { background: white !important; }
  .print-container { padding: 0 !important; }
}
`;

// ─── Camper sorting ───────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: "name", label: "Name" },
  { value: "yearsInCamp", label: "Yrs in Camp" },
  { value: "totalAcs", label: "Total ACs" },
  { value: "totalHonors", label: "Total Honors" },
  { value: "currentYearAcs", label: "Yr ACs" },
  { value: "currentYearHonors", label: "Yr Honors" },
] as const;

type SortKey = (typeof SORT_OPTIONS)[number]["value"];
type SortDir = "asc" | "desc";
interface SortLevel {
  key: SortKey;
  dir: SortDir;
}

const DEFAULT_SORT_LEVELS: [SortLevel, SortLevel, SortLevel] = [
  { key: "totalAcs", dir: "asc" },
  { key: "currentYearAcs", dir: "asc" },
  { key: "yearsInCamp", dir: "asc" },
];

function compareByKey(a: CeremonyReportCamper, b: CeremonyReportCamper, key: SortKey): number {
  if (key === "name") {
    return a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);
  }
  return a[key] - b[key];
}

function sortCampers(campers: CeremonyReportCamper[], sortLevels: SortLevel[]): CeremonyReportCamper[] {
  return [...campers].sort((a, b) => {
    for (const { key, dir } of sortLevels) {
      const cmp = compareByKey(a, b, key);
      if (cmp !== 0) return dir === "desc" ? -cmp : cmp;
    }
    // Stable final tiebreaker so equal rows always list alphabetically
    return compareByKey(a, b, "name");
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Zeros display as blanks so the printed report only shows meaningful counts. */
function blankZero(value: number): string | number {
  return value === 0 ? "" : value;
}

function CamperRow({ camper }: { camper: CeremonyReportCamper }) {
  const isFirstYear = camper.yearsInCamp === 1;
  return (
    <tr className="border-t border-stone-200">
      <td className="py-1 pr-3 font-medium">{camper.firstName} {camper.lastName}</td>
      <td className="py-1 px-2 text-center tabular-nums">{blankZero(camper.yearsInCamp)}</td>
      <td className="py-1 px-2 text-center tabular-nums">{isFirstYear ? "" : blankZero(camper.totalAcs)}</td>
      <td className="py-1 px-2 text-center tabular-nums">{isFirstYear ? "" : blankZero(camper.totalHonors)}</td>
      <td className="py-1 px-2 text-center tabular-nums">{blankZero(camper.currentYearAcs)}</td>
      <td className="py-1 pl-2 text-center tabular-nums">{blankZero(camper.currentYearHonors)}</td>
    </tr>
  );
}

function CabinSection({
  cabin,
  isFirstTerm,
  sortLevels,
}: {
  cabin: CeremonyReportCabin;
  isFirstTerm: boolean;
  sortLevels: SortLevel[];
}) {
  // On the 1st term report, full-term campers are excluded from the main table
  // and listed below counselors so staff can confirm they weren't accidentally omitted.
  const mainCampers = sortCampers(
    isFirstTerm ? cabin.campers.filter((c) => !c.isFullTerm) : cabin.campers,
    sortLevels,
  );
  const fullTermCampers = isFirstTerm
    ? sortCampers(cabin.campers.filter((c) => c.isFullTerm), sortLevels)
    : [];

  if (mainCampers.length === 0 && cabin.counselors.length === 0 && fullTermCampers.length === 0) {
    return null;
  }

  return (
    <div className="cabin-section border border-stone-500 rounded mb-3 p-2">
      <div className="font-bold mb-1">{formatCabinNumber(cabin.name)}</div>
      {mainCampers.length > 0 && (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-stone-400">
              <th className="pb-1 pr-3 text-left font-semibold">Name</th>
              <th className="pb-1 px-2 text-center font-semibold whitespace-nowrap">Yrs in Camp</th>
              <th className="pb-1 px-2 text-center font-semibold whitespace-nowrap">Total ACs</th>
              <th className="pb-1 px-2 text-center font-semibold whitespace-nowrap">Total Honors</th>
              <th className="pb-1 px-2 text-center font-semibold whitespace-nowrap">Yr ACs</th>
              <th className="pb-1 pl-2 text-center font-semibold whitespace-nowrap">Yr Honors</th>
            </tr>
          </thead>
          <tbody>
            {mainCampers.map((camper) => (
              <CamperRow key={camper.id} camper={camper} />
            ))}
          </tbody>
        </table>
      )}
      {cabin.counselors.length > 0 && (
        <div className="mt-2 text-stone-600 italic">
          Counselors: {cabin.counselors.join(", ")}
        </div>
      )}
      {fullTermCampers.length > 0 && (
        <div className="mt-2 text-stone-500 text-sm">
          <span className="font-semibold not-italic">Full Term:</span>{" "}
          {fullTermCampers.map((c) => `${c.firstName} ${c.lastName}`).join(", ")}
        </div>
      )}
    </div>
  );
}

function DivisionSection({
  division,
  fontSize,
  divRef,
  isFirstTerm,
  sortLevels,
}: {
  division: CeremonyReportDivision;
  fontSize: number;
  divRef: (el: HTMLElement | null) => void;
  isFirstTerm: boolean;
  sortLevels: SortLevel[];
}) {
  return (
    <section
      className="division-section pt-4"
      ref={divRef}
      style={{ fontSize: `${fontSize}pt` }}
    >
      <h2 className="font-bold text-center mb-3" style={{ fontSize: `${fontSize + 4}pt` }}>
        {normalizeDivisionName(division.name)}
      </h2>
      {division.cabins
        .filter((c) => parseCabinNumber(c.name) != null)
        .sort((a, b) => (parseCabinNumber(b.name) ?? 0) - (parseCabinNumber(a.name) ?? 0))
        .map((cabin) => (
          <CabinSection key={cabin.id} cabin={cabin} isFirstTerm={isFirstTerm} sortLevels={sortLevels} />
        ))}
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function KerchiefCeremonyReport() {
  const { selectedTermId, selectedYear, selectedTerm } = useSeason();
  const [divisions, setDivisions] = useState<CeremonyReportDivision[]>([]);
  const [isFirstTerm, setIsFirstTerm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fontSizes, setFontSizes] = useState<Record<number, number>>({});
  const [sortLevels, setSortLevels] = useState<[SortLevel, SortLevel, SortLevel]>(DEFAULT_SORT_LEVELS);

  const divRefs = useRef<Record<number, HTMLElement | null>>({});

  // Inject print CSS once
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = PRINT_STYLE;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Load data when term changes
  useEffect(() => {
    if (selectedTermId === null) {
      setDivisions([]);
      setIsFirstTerm(false);
      return;
    }
    setLoading(true);
    setFontSizes({});
    reportsApi
      .ceremony(selectedTermId, selectedYear)
      .then((response) => {
        if (response) {
          setDivisions(response.divisions);
          setIsFirstTerm(response.isFirstTerm);
        }
      })
      .catch(() => toast.error("Failed to load ceremony report"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTermId, selectedYear]);

  // Auto-size fonts after divisions render
  useEffect(() => {
    if (loading || divisions.length === 0) return;

    const sizes: Record<number, number> = {};

    for (const division of divisions) {
      const el = divRefs.current[division.id];
      if (!el) continue;

      let fontSize = FONT_SIZE_MAX_PT;
      el.style.fontSize = `${fontSize}pt`;
      el.style.maxWidth = `${PRINT_PAGE_WIDTH_PX}px`;

      while (
        fontSize > FONT_SIZE_MIN_PT &&
        (el.scrollHeight > PRINT_PAGE_HEIGHT_PX || el.scrollWidth > PRINT_PAGE_WIDTH_PX)
      ) {
        fontSize--;
        el.style.fontSize = `${fontSize}pt`;
      }

      el.style.maxWidth = "";
      sizes[division.id] = fontSize;
    }

    setFontSizes(sizes);
  }, [loading, divisions]);

  const setDivRef = useCallback((id: number) => (el: HTMLElement | null) => {
    divRefs.current[id] = el;
  }, []);

  return (
    <div>
      <PageHeader title={`Kerchief Ceremony Report${selectedTerm ? ` — ${selectedTerm.year} ${selectedTerm.name}` : ""}`} />

      {/* Controls — hidden on print */}
      <div className="no-print flex flex-wrap items-center gap-4 mb-6 px-6">
        <button
          onClick={() => window.print()}
          disabled={selectedTermId === null || divisions.length === 0}
          className="flex items-center gap-1.5 rounded-md bg-forest-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-forest-700 focus:outline-none focus:ring-2 focus:ring-forest-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <PrinterIcon className="w-4 h-4" />
          Print / Save as PDF
        </button>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-stone-700">Sort by</span>
          {sortLevels.map((level, index) => (
            <div key={index} className="flex items-center gap-1">
              <select
                value={level.key}
                aria-label={`Sort level ${index + 1} field`}
                onChange={(e) => {
                  const next = [...sortLevels] as [SortLevel, SortLevel, SortLevel];
                  next[index] = { ...next[index], key: e.target.value as SortKey };
                  setSortLevels(next);
                }}
                className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                value={level.dir}
                aria-label={`Sort level ${index + 1} direction`}
                onChange={(e) => {
                  const next = [...sortLevels] as [SortLevel, SortLevel, SortLevel];
                  next[index] = { ...next[index], dir: e.target.value as SortDir };
                  setSortLevels(next);
                }}
                className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Report content */}
      <div className="print-container px-6">
        {selectedTermId === null ? (
          <div className="no-print text-center py-16 text-stone-500">
            Select a specific session to view the Kerchief Ceremony Report.
          </div>
        ) : loading ? (
          <div className="no-print flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : divisions.length === 0 ? (
          <div className="no-print text-center py-16 text-stone-500">
            No data found for this term. Make sure campers are enrolled and assigned to cabins in a division.
          </div>
        ) : (
          divisions
            .map((division) => ({
              division,
              maxCabin: Math.max(
                0,
                ...division.cabins.map((c) => parseCabinNumber(c.name) ?? 0),
              ),
            }))
            .filter((d) => d.maxCabin > 0)
            .sort((a, b) => b.maxCabin - a.maxCabin)
            .map(({ division }) => (
              <DivisionSection
                key={division.id}
                division={division}
                fontSize={fontSizes[division.id] ?? FONT_SIZE_MAX_PT}
                divRef={setDivRef(division.id)}
                isFirstTerm={isFirstTerm}
                sortLevels={sortLevels}
              />
            ))
        )}
      </div>
    </div>
  );
}
